import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Trophy, Flag, Clock, TrendingUp, Zap, Medal, Calendar, Link2, Check, Trash2, Users, ChevronDown } from 'lucide-react';
import api from '../utils/api';

// Share Link Panel
const ShareLinkPanel = () => {
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/f1/share-link').then(r => {
      setToken(r.data.token || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const createLink = async () => {
    try {
      const r = await api.post('/f1/share-link');
      setToken(r.data.token);
    } catch (e) {
      console.error(e);
    }
  };

  const revokeLink = async () => {
    try {
      await api.delete('/f1/share-link');
      setToken(null);
    } catch (e) {
      console.error(e);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/f1/live/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return null;

  return (
    <div className="bg-gray-900/50 rounded-xl p-4 mb-6 border border-gray-700" data-testid="f1-share-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-white">
          <Link2 className="w-5 h-5 text-blue-400" />
          <span className="font-medium">Offentligt delelink</span>
          <span className="text-xs text-gray-400">(kræver ikke login)</span>
        </div>
        {token ? (
          <div className="flex items-center gap-2">
            <code className="bg-gray-800 text-gray-300 px-3 py-1.5 rounded text-sm max-w-xs truncate" data-testid="f1-share-url">
              {window.location.origin}/f1/live/{token}
            </code>
            <button
              onClick={copyLink}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                copied ? 'bg-green-600 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'
              }`}
              data-testid="f1-copy-link-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : 'Kopier'}
            </button>
            <button
              onClick={revokeLink}
              className="p-1.5 rounded text-red-400 hover:bg-red-500/20 transition-colors"
              title="Deaktiver link"
              data-testid="f1-revoke-link-btn"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={createLink}
            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-1.5 rounded text-sm font-medium transition-colors"
            data-testid="f1-create-link-btn"
          >
            Opret delelink
          </button>
        )}
      </div>
    </div>
  );
};

// F1 Car Component with animation
// Driver images mapping (shared across components)
const driverImages = {
  'Jakob': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/p6nmp3k7_Jakob.jpg',
  'Jacob': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/p6nmp3k7_Jakob.jpg',
  'Jacob Berg': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/p6nmp3k7_Jakob.jpg',
  'Landtow': 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/t1fofedo_Gemini_Generated_Image_rugb66rugb66rugb.png',
  'Lantow': 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/t1fofedo_Gemini_Generated_Image_rugb66rugb66rugb.png',
  'Kenneth': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/6s0wdtje_Kenneth.jpg',
  'Oscar': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/ceu99zmm_Oscar.jpg',
  'Havemann': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/ghgp5yr6_Havemann.jpg',
  'Tobias': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/tnsywa6z_Tobias.jpg',
  'Lisette': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/23g4b1aw_Lisette.jpg',
  'Martin': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/0c29qbv2_Martin.jpg',
  'Allan': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/esb9gnrt_Allan.jpg',
  'Lennarth': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/pcvmgwaa_Lennarth.jpg',
  'Christopher': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/l6s0zvdi_Christopher.jpg',
  'Noah': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/ugpj1585_Noah.jpg',
  'Mikkel': 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/3kxydne2_Gemini_Generated_Image_83qvov83qvov83qv.png',
  'Oscar D.': 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/93937d76_Oscar%20D.jpg',
  'Benny': 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/czjh4og7_Gemini_Generated_Image_w0zijow0zijow0zi.png',
  'Guldbrandsen': 'https://customer-assets.emergentagent.com/job_7cd94266-aaca-42b4-8274-f9ae4208543e/artifacts/2yec949a_IMG_2494.png',
  'Blichfeldt': 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/whcxo8ql_Gemini_Generated_Image_mhekr2mhekr2mhek.png',
  'Lasse': 'https://customer-assets.emergentagent.com/job_7cd94266-aaca-42b4-8274-f9ae4208543e/artifacts/e9sj6l8m_IMG_2496.png',
  'Emil': 'https://customer-assets.emergentagent.com/job_7cd94266-aaca-42b4-8274-f9ae4208543e/artifacts/5yq06gjn_IMG_2498.png',
  'Emil H': 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/wxyo40ao_Gemini_Generated_Image_kseoydkseoydkseo.png',
  'Emil Hansen': 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/wxyo40ao_Gemini_Generated_Image_kseoydkseoydkseo.png',
  'Malthe': 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/81invhzi_Gemini_Generated_Image_vcwoy6vcwoy6vcwo.png',
};

const getDriverImage = (name) => {
  if (!name) return null;
  if (driverImages[name]) return driverImages[name];
  const key = Object.keys(driverImages).find(k => 
    name.toLowerCase().includes(k.toLowerCase()) || k.toLowerCase().includes(name.toLowerCase())
  );
  return key ? driverImages[key] : null;
};

const F1Car = ({ racer, index, isLeader }) => {
  const position = racer.track_position * 100;
  const colors = ['#FF1801', '#00D2BE', '#0600EF', '#F596C8', '#FF8700', '#006F62', '#2B4562', '#B6BABD', '#469BFF', '#1B3D67'];
  const carColor = colors[index % colors.length];
  
  // Darker shade for shadows/details
  const darkerColor = carColor + '99';
  
  return (
    <div className="relative h-16 mb-2 group">
      {/* Lane background */}
      <div className="absolute inset-0 bg-gradient-to-r from-gray-800/50 via-gray-700/30 to-gray-800/50 rounded-lg border border-gray-700/50">
        {/* Track markings */}
        <div className="absolute inset-y-0 left-0 right-16 flex justify-between px-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="w-0.5 h-full bg-gray-600/30" />
          ))}
        </div>
      </div>
      
      {/* Finish line */}
      <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-b from-white via-black to-white bg-[length:100%_8px] bg-repeat-y rounded-r-lg" />
      
      {/* Car */}
      <div 
        className="absolute top-1/2 -translate-y-1/2 transition-all duration-1000 ease-out flex items-center"
        style={{ left: `calc(${Math.min(position, 95)}% - 50px)` }}
      >
        {/* DRS indicator */}
        {racer.drs_active && (
          <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-green-500 text-xs px-2 py-0.5 rounded-full text-white font-bold animate-pulse shadow-lg shadow-green-500/50">
            DRS
          </div>
        )}
        
        {/* Speed lines when leading or DRS */}
        {(isLeader || racer.drs_active) && (
          <div className="absolute -left-10 top-1/2 -translate-y-1/2 flex flex-col gap-1">
            <div className="w-6 h-0.5 bg-gradient-to-r from-transparent to-yellow-400 rounded animate-pulse" />
            <div className="w-9 h-0.5 bg-gradient-to-r from-transparent to-orange-400 rounded animate-pulse" />
            <div className="w-7 h-0.5 bg-gradient-to-r from-transparent to-yellow-400 rounded animate-pulse" />
          </div>
        )}
        
        {/* Detailed F1 Car SVG */}
        <svg width="70" height="28" viewBox="0 0 100 40" className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
          <defs>
            {/* Gradient for car body */}
            <linearGradient id={`carGrad${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={carColor} />
              <stop offset="50%" stopColor={carColor} />
              <stop offset="100%" stopColor={darkerColor} />
            </linearGradient>
            {/* Metallic wheel gradient */}
            <radialGradient id={`wheelGrad${index}`}>
              <stop offset="0%" stopColor="#4a4a4a" />
              <stop offset="60%" stopColor="#1a1a1a" />
              <stop offset="100%" stopColor="#000" />
            </radialGradient>
            {/* Tire gradient */}
            <radialGradient id={`tireGrad${index}`}>
              <stop offset="70%" stopColor="#2a2a2a" />
              <stop offset="100%" stopColor="#1a1a1a" />
            </radialGradient>
          </defs>
          
          {/* Shadow under car */}
          <ellipse cx="50" cy="36" rx="35" ry="3" fill="rgba(0,0,0,0.3)" />
          
          {/* Rear wing */}
          <rect x="2" y="8" width="3" height="24" rx="1" fill="#222" />
          <rect x="5" y="5" width="12" height="4" rx="1" fill={carColor} />
          <rect x="5" y="31" width="12" height="4" rx="1" fill={carColor} />
          <rect x="5" y="9" width="2" height="22" fill="#333" />
          
          {/* Main body - sidepod */}
          <path d={`M 18 12 L 75 14 Q 85 16 88 20 Q 85 24 75 26 L 18 28 Q 12 20 18 12`} fill={`url(#carGrad${index})`} />
          
          {/* Engine cover / airbox */}
          <path d="M 20 12 L 22 6 L 32 6 L 35 12" fill={carColor} />
          <rect x="24" y="4" width="6" height="3" rx="1" fill="#222" />
          
          {/* Halo */}
          <path d="M 38 10 Q 45 4 52 10" stroke="#555" strokeWidth="2.5" fill="none" />
          <path d="M 38 10 Q 45 5 52 10" stroke="#333" strokeWidth="1.5" fill="none" />
          
          {/* Cockpit */}
          <ellipse cx="45" cy="20" rx="10" ry="6" fill="#111" />
          <ellipse cx="45" cy="19" rx="7" ry="4" fill="#1a1a1a" />
          {/* Driver helmet */}
          <ellipse cx="45" cy="18" rx="4" ry="3" fill={carColor} />
          <ellipse cx="46" cy="17" rx="2" ry="1.5" fill="#222" />
          
          {/* Sidepod details */}
          <rect x="55" y="15" width="15" height="1" fill="rgba(255,255,255,0.3)" />
          <rect x="55" y="24" width="15" height="1" fill="rgba(0,0,0,0.3)" />
          
          {/* Front nose */}
          <path d="M 85 18 L 98 19 L 98 21 L 85 22 Z" fill={carColor} />
          <path d="M 95 19 L 98 19.5 L 98 20.5 L 95 21 Z" fill="#222" />
          
          {/* Front wing */}
          <rect x="88" y="10" width="10" height="3" rx="1" fill={carColor} />
          <rect x="88" y="27" width="10" height="3" rx="1" fill={carColor} />
          <rect x="94" y="13" width="4" height="14" rx="1" fill="#333" />
          <rect x="88" y="12" width="2" height="16" fill={carColor} />
          
          {/* Front wheels */}
          <ellipse cx="82" cy="20" rx="5" ry="8" fill={`url(#tireGrad${index})`} />
          <ellipse cx="82" cy="20" rx="3" ry="5" fill={`url(#wheelGrad${index})`} />
          <ellipse cx="82" cy="20" rx="1.5" ry="2.5" fill="#555" />
          
          {/* Rear wheels */}
          <ellipse cx="22" cy="20" rx="6" ry="9" fill={`url(#tireGrad${index})`} />
          <ellipse cx="22" cy="20" rx="4" ry="6" fill={`url(#wheelGrad${index})`} />
          <ellipse cx="22" cy="20" rx="2" ry="3" fill="#555" />
          
          {/* Number on car */}
          <circle cx="60" cy="20" r="6" fill="white" />
          <text x="60" y="24" textAnchor="middle" fill="#111" fontSize="10" fontWeight="bold">
            {racer.position}
          </text>
          
          {/* Exhaust glow when leading */}
          {isLeader && (
            <>
              <ellipse cx="8" cy="20" rx="4" ry="2" fill="orange" opacity="0.6" />
              <ellipse cx="6" cy="20" rx="2" ry="1" fill="yellow" opacity="0.8" />
            </>
          )}
        </svg>
        
        {/* Name tag */}
        <div className="ml-2 bg-black/90 px-2 py-1.5 rounded text-xs font-medium whitespace-nowrap hidden group-hover:block border border-gray-600">
          <div className="text-white font-semibold">{racer.name}</div>
          {racer.on_leave ? (
            <div className="text-yellow-500">Fravær</div>
          ) : (
            <>
              <div className="text-green-400">DPT: {racer.dpt.toLocaleString('da-DK')}</div>
              <div className="text-blue-400">DB: {racer.db?.toLocaleString('da-DK')} kr</div>
              <div className="text-gray-400">Timer: {racer.hours?.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</div>
            </>
          )}
        </div>
      </div>
      
      {/* Position badge */}
      <div className={`absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        racer.position === 1 ? 'bg-yellow-500 text-black' :
        racer.position === 2 ? 'bg-gray-300 text-black' :
        racer.position === 3 ? 'bg-amber-600 text-white' :
        'bg-gray-700 text-white'
      }`}>
        {racer.position}
      </div>
      
      {/* Racer name at start */}
      <div className="absolute left-10 top-1/2 -translate-y-1/2 text-xs text-white/70 truncate max-w-24">
        {racer.name.split(' ')[0]}
      </div>
    </div>
  );
};

// Podium component
const Podium = ({ racers }) => {
  if (!racers || racers.length < 3) return null;
  
  const [first, second, third] = racers;
  
  return (
    <div className="flex items-end justify-center gap-2 h-48 mt-8">
      {/* 2nd place */}
      <div className="flex flex-col items-center">
        <div className="text-gray-300 text-sm mb-2 truncate max-w-20">{second?.name?.split(' ')[0]}</div>
        <div className="text-gray-400 text-xs mb-1">DPT: {second?.dpt?.toLocaleString('da-DK')}</div>
        <div className="w-20 h-24 bg-gradient-to-t from-gray-400 to-gray-300 rounded-t-lg flex items-center justify-center">
          <span className="text-4xl font-bold text-gray-700">2</span>
        </div>
      </div>
      
      {/* 1st place */}
      <div className="flex flex-col items-center">
        <Trophy className="w-8 h-8 text-yellow-500 mb-1" />
        <div className="text-yellow-500 font-bold mb-1 truncate max-w-24">{first?.name?.split(' ')[0]}</div>
        <div className="text-yellow-400 text-xs mb-1">DPT: {first?.dpt?.toLocaleString('da-DK')}</div>
        <div className="w-24 h-32 bg-gradient-to-t from-yellow-600 to-yellow-400 rounded-t-lg flex items-center justify-center">
          <span className="text-5xl font-bold text-yellow-900">1</span>
        </div>
      </div>
      
      {/* 3rd place */}
      <div className="flex flex-col items-center">
        <div className="text-amber-600 text-sm mb-2 truncate max-w-20">{third?.name?.split(' ')[0]}</div>
        <div className="text-amber-500 text-xs mb-1">DPT: {third?.dpt?.toLocaleString('da-DK')}</div>
        <div className="w-20 h-20 bg-gradient-to-t from-amber-700 to-amber-500 rounded-t-lg flex items-center justify-center">
          <span className="text-4xl font-bold text-amber-900">3</span>
        </div>
      </div>
    </div>
  );
};

// Leaderboard table
const Leaderboard = ({ racers, title }) => (
  <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700">
    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
      <Flag className="w-5 h-5 text-red-500" />
      {title}
    </h3>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-400 border-b border-gray-700">
            <th className="text-left py-2 px-2">Pos</th>
            <th className="text-left py-2">Konsulent</th>
            <th className="text-right py-2 px-2">DPT</th>
          </tr>
        </thead>
        <tbody>
          {racers?.map((racer, idx) => (
            <tr key={idx} className={`border-b border-gray-800 ${
              racer.position === 1 ? 'bg-yellow-500/10' :
              racer.position === 2 ? 'bg-gray-400/10' :
              racer.position === 3 ? 'bg-amber-600/10' : ''
            }`}>
              <td className="py-2 px-2">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                  racer.position === 1 ? 'bg-yellow-500 text-black' :
                  racer.position === 2 ? 'bg-gray-300 text-black' :
                  racer.position === 3 ? 'bg-amber-600 text-white' :
                  'bg-gray-700 text-white'
                }`}>
                  {racer.position}
                </span>
              </td>
              <td className="py-2 text-white font-medium">
                {racer.name}
                {racer.on_leave && <span className="ml-2 text-xs text-yellow-500/70">(Fravær)</span>}
              </td>
              <td className="py-2 text-right px-2 text-green-400 font-mono">{racer.dpt?.toLocaleString('da-DK')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Championship standings
const ChampionshipStandings = ({ standings, calendar }) => {
  const [selectedRace, setSelectedRace] = React.useState('total');
  const [expandedDriver, setExpandedDriver] = React.useState(null);

  const completedRaces = (calendar || []).filter(r => r.status === 'completed');
  const activeRace = (calendar || []).find(r => r.status === 'active');

  // Build display data based on selection
  const displayData = React.useMemo(() => {
    if (selectedRace === 'total') {
      return standings?.map(s => ({ ...s, display_points: s.total_points || 0 })) || [];
    }
    
    const weekNum = parseInt(selectedRace);
    const raceStandings = [];
    (standings || []).forEach(s => {
      const raceEntry = (s.races || []).find(r => r.week === weekNum);
      if (raceEntry) {
        raceStandings.push({
          name: s.name,
          display_points: raceEntry.points || 0,
          position: raceEntry.position,
          dpt: raceEntry.dpt,
          bonus: raceEntry.bonus || 0
        });
      }
    });
    raceStandings.sort((a, b) => a.position - b.position);
    return raceStandings;
  }, [selectedRace, standings]);

  const raceName = (calendar || []).find(r => String(r.week_number) === selectedRace)?.race_name;

  return (
    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          Mesterskabsstilling
        </h3>
        <select
          data-testid="championship-race-select"
          value={selectedRace}
          onChange={(e) => setSelectedRace(e.target.value)}
          className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-red-500"
        >
          <option value="total">Samlet stilling</option>
          {completedRaces.map(r => (
            <option key={r.week_number} value={String(r.week_number)}>
              Løb {r.week_number - 15} — {r.race_name} (Uge {r.week_number})
            </option>
          ))}
          {activeRace && (
            <option value={String(activeRace.week_number)}>
              Løb {activeRace.week_number - 15} — {activeRace.race_name} (Uge {activeRace.week_number}) — Live
            </option>
          )}
        </select>
      </div>
      {selectedRace !== 'total' && raceName && (
        <p className="text-gray-400 text-xs mb-3">{raceName} — Uge {selectedRace}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 border-b border-gray-700">
              <th className="text-left py-2 px-2">Pos</th>
              <th className="text-left py-2">Konsulent</th>
              {selectedRace !== 'total' && <th className="text-right py-2 px-2">DPT</th>}
              {selectedRace !== 'total' && <th className="text-right py-2 px-2">Bonus</th>}
              <th className="text-right py-2 px-2">Point</th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((s, idx) => {
              const isExpanded = selectedRace === 'total' && expandedDriver === s.name;
              const fullDriver = (standings || []).find(st => st.name === s.name);
              const driverRaces = fullDriver?.races || [];
              return (
                <React.Fragment key={idx}>
                  <tr className={`border-b border-gray-800 ${
                    (s.position || idx + 1) === 1 ? 'bg-yellow-500/10' :
                    (s.position || idx + 1) === 2 ? 'bg-gray-400/10' :
                    (s.position || idx + 1) === 3 ? 'bg-amber-600/10' : ''
                  } ${selectedRace === 'total' ? 'cursor-pointer hover:bg-white/5' : ''}`}
                    onClick={() => {
                      if (selectedRace !== 'total') return;
                      setExpandedDriver(expandedDriver === s.name ? null : s.name);
                    }}
                    data-testid={`championship-row-${s.name?.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <td className="py-2 px-2">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        (s.position || idx + 1) === 1 ? 'bg-yellow-500 text-black' :
                        (s.position || idx + 1) === 2 ? 'bg-gray-300 text-black' :
                        (s.position || idx + 1) === 3 ? 'bg-amber-600 text-white' :
                        'bg-gray-700 text-white'
                      }`}>
                        {s.position || idx + 1}
                      </span>
                    </td>
                    <td className="py-2 text-white font-medium">
                      <span className="flex items-center gap-2">
                        {s.name}
                        {selectedRace === 'total' && (
                          <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                      </span>
                    </td>
                    {selectedRace !== 'total' && <td className="py-2 text-right px-2 text-gray-400">{s.dpt?.toLocaleString('da-DK') || 0}</td>}
                    {selectedRace !== 'total' && <td className="py-2 text-right px-2 text-green-400">{s.bonus > 0 ? `+${s.bonus}` : ''}</td>}
                    <td className="py-2 text-right px-2 text-yellow-400 font-bold">{s.display_points || 0}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-black/40 border-b border-gray-800">
                      <td colSpan={3} className="p-4">
                        <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Løbsberegning — {s.name}</div>
                        {driverRaces.length === 0 ? (
                          <div className="text-sm text-white/40 italic">Ingen afsluttede løb endnu.</div>
                        ) : (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-white/40 border-b border-white/10">
                                <th className="text-left py-1.5 px-2">Løb</th>
                                <th className="text-right py-1.5 px-2">Pos</th>
                                <th className="text-right py-1.5 px-2">DB</th>
                                <th className="text-right py-1.5 px-2">Timer</th>
                                <th className="text-right py-1.5 px-2">DPT</th>
                                <th className="text-right py-1.5 px-2">Bonus</th>
                                <th className="text-right py-1.5 px-2">Point</th>
                              </tr>
                            </thead>
                            <tbody>
                              {driverRaces.map((r, ri) => (
                                <tr key={ri} className="border-b border-white/[0.04]">
                                  <td className="py-1.5 px-2 text-white/80">
                                    {r.race_name || `Uge ${r.week}`}
                                    <span className="text-white/30 ml-1">(Uge {r.week})</span>
                                  </td>
                                  <td className="py-1.5 px-2 text-right text-white/70">{r.position}</td>
                                  <td className="py-1.5 px-2 text-right text-sky-400/80">{(r.db || 0).toLocaleString('da-DK')} kr</td>
                                  <td className="py-1.5 px-2 text-right text-white/60">{(r.hours || 0).toFixed(1)}</td>
                                  <td className="py-1.5 px-2 text-right text-white/60">{(r.dpt || 0).toLocaleString('da-DK')}</td>
                                  <td className="py-1.5 px-2 text-right text-green-400">{r.bonus ? `+${r.bonus}` : ''}</td>
                                  <td className="py-1.5 px-2 text-right text-yellow-400 font-bold">{r.points || 0}</td>
                                </tr>
                              ))}
                              <tr className="bg-yellow-500/5">
                                <td colSpan={6} className="py-2 px-2 text-right text-white/70 font-semibold">Samlet</td>
                                <td className="py-2 px-2 text-right text-yellow-400 font-bold">
                                  {driverRaces.reduce((sum, r) => sum + (r.points || 0), 0)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </td>
                      <td colSpan={selectedRace !== 'total' ? 2 : 0}></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Race Calendar (simple version for overview)
const RaceCalendarSimple = ({ races }) => (
  <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700">
    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
      <Calendar className="w-5 h-5 text-blue-400" />
      Løbskalender
    </h3>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {races?.map((race, idx) => (
        <div key={idx} className={`p-3 rounded-lg border ${
          race.status === 'active' ? 'bg-green-500/20 border-green-500' :
          race.status === 'completed' ? 'bg-gray-700/50 border-gray-600' :
          'bg-gray-800/50 border-gray-700'
        }`}>
          <div className="text-xs text-gray-400">{race.race_name}</div>
          <div className="text-sm text-white font-medium mt-1">
            {race.start_date?.split('-').reverse().join('/')} - {race.end_date?.split('-').reverse().slice(0, 2).join('/')}
          </div>
          {race.status === 'active' && (
            <div className="text-xs text-green-400 mt-1 flex items-center gap-1">
              <Zap className="w-3 h-3" /> LIVE
            </div>
          )}
          {race.status === 'completed' && race.winner && (
            <div className="text-xs text-yellow-400 mt-1">
              Vinder: {race.winner}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// Full Race Calendar Tab Component
const RaceCalendarTab = ({ races }) => {
  // Prize structure
  // Prize mapping
  const prizes = {
    1: { prize: '500 kr. gavekort', icon: '🥇', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30' },
    2: { prize: '300 kr. gavekort', icon: '🥈', color: 'text-gray-300', bg: 'bg-gray-400/20', border: 'border-gray-500/30' },
    3: { prize: '200 kr. gavekort', icon: '🥉', color: 'text-amber-500', bg: 'bg-amber-500/20', border: 'border-amber-500/30' },
  };

  const completedRaces = races?.filter(r => r.status === 'completed') || [];
  const activeRace = races?.find(r => r.status === 'active');
  const upcomingRaces = races?.filter(r => r.status === 'upcoming') || [];

  // Podium card component with driver image
  const PodiumCard = ({ position, name, prizeInfo }) => {
    const image = getDriverImage(name);
    return (
      <div className={`${prizeInfo.bg} rounded-lg p-4 border ${prizeInfo.border}`}>
        <div className="flex items-center gap-3">
          {image ? (
            <div className="relative">
              <img 
                src={image} 
                alt={name || 'Unknown'} 
                className="w-16 h-16 rounded-full object-cover border-2"
                style={{ borderColor: prizeInfo.color.replace('text-', '').includes('yellow') ? '#EAB308' : 
                         prizeInfo.color.includes('gray') ? '#9CA3AF' : '#F59E0B' }}
              />
              <div className="absolute -top-1 -right-1 text-xl">{prizeInfo.icon}</div>
            </div>
          ) : (
            <div className="text-4xl">{prizeInfo.icon}</div>
          )}
          <div className="flex-1">
            <div className={`text-xs ${prizeInfo.color}`}>{position}. plads</div>
            <div className="text-white font-bold text-lg">{name || '-'}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Løbskalender 2025</h2>
        <p className="text-gray-400">Gardinbussen Championship - Alle løb og vindere</p>
      </div>

      {/* Active Race */}
      {activeRace && (
        <div className="bg-gradient-to-r from-green-900/30 via-gray-900/50 to-green-900/30 rounded-xl p-6 border border-green-500/50">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            <h3 className="text-lg font-bold text-white">Aktivt løb</h3>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 border border-green-500/30">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-2xl font-bold text-white">{activeRace.race_name}</div>
                <div className="text-gray-400 mt-1">
                  {activeRace.start_date?.split('-').reverse().join('/')} - {activeRace.end_date?.split('-').reverse().join('/')}
                </div>
              </div>
              <div className="flex items-center gap-2 bg-green-500/20 px-4 py-2 rounded-full">
                <Zap className="w-4 h-4 text-green-400" />
                <span className="text-green-400 font-bold">LIVE</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completed Races */}
      {completedRaces.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Flag className="w-5 h-5 text-gray-400" />
            Afsluttede løb
          </h3>
          <div className="space-y-4">
            {completedRaces.map((race, idx) => (
              <div key={idx} className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-700">
                <div className="bg-gray-800/50 p-4 border-b border-gray-700">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-lg font-bold text-white">{race.race_name}</div>
                      <div className="text-sm text-gray-400">
                        {race.start_date?.split('-').reverse().join('/')} - {race.end_date?.split('-').reverse().join('/')}
                      </div>
                    </div>
                    <div className="bg-gray-700 px-3 py-1 rounded-full text-xs text-gray-300">
                      Afsluttet
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <PodiumCard position={1} name={race.winner} prizeInfo={prizes[1]} />
                    <PodiumCard position={2} name={race.second} prizeInfo={prizes[2]} />
                    <PodiumCard position={3} name={race.third} prizeInfo={prizes[3]} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Races */}
      {upcomingRaces.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Kommende løb
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingRaces.map((race, idx) => (
              <div key={idx} className="bg-gray-900/50 rounded-xl p-4 border border-gray-700 hover:border-blue-500/50 transition-all">
                <div className="text-lg font-bold text-white mb-2">{race.race_name}</div>
                <div className="text-sm text-gray-400">
                  {race.start_date?.split('-').reverse().join('/')} - {race.end_date?.split('-').reverse().join('/')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Teams and Drivers Component
const TeamsAndDrivers = () => {
  const teams = [
    {
      name: 'Red Bull Racing',
      color: '#1B3D67',
      accentColor: '#FF1801',
      logo: '🐂',
      drivers: [
        {
          name: 'Jakob',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/p6nmp3k7_Jakob.jpg',
          number: 1
        },
        {
          name: 'Landtow',
          image: 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/t1fofedo_Gemini_Generated_Image_rugb66rugb66rugb.png',
          number: 11,
          objectPos: '50% 30%'
        },
        {
          name: 'Emil',
          image: 'https://customer-assets.emergentagent.com/job_7cd94266-aaca-42b4-8274-f9ae4208543e/artifacts/5yq06gjn_IMG_2498.png',
          number: 33
        }
      ]
    },
    {
      name: 'Mercedes-AMG Petronas',
      color: '#00D2BE',
      accentColor: '#000000',
      logo: '⭐',
      drivers: [
        {
          name: 'Kenneth',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/6s0wdtje_Kenneth.jpg',
          number: 44
        },
        {
          name: 'Oscar',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/ceu99zmm_Oscar.jpg',
          number: 63
        },
        {
          name: 'Emil H',
          image: 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/wxyo40ao_Gemini_Generated_Image_kseoydkseoydkseo.png',
          number: 7
        }
      ]
    },
    {
      name: 'Scuderia Ferrari',
      color: '#DC0000',
      accentColor: '#FFF500',
      logo: '🐎',
      drivers: [
        {
          name: 'Havemann',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/ghgp5yr6_Havemann.jpg',
          number: 16
        },
        {
          name: 'Tobias',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/tnsywa6z_Tobias.jpg',
          number: 55
        }
      ]
    },
    {
      name: 'McLaren F1 Team',
      color: '#FF8700',
      accentColor: '#47C7FC',
      logo: '🏎️',
      drivers: [
        {
          name: 'Lisette',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/23g4b1aw_Lisette.jpg',
          number: 4
        },
        {
          name: 'Martin',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/0c29qbv2_Martin.jpg',
          number: 81
        }
      ]
    },
    {
      name: 'Aston Martin Aramco',
      color: '#006F62',
      accentColor: '#CEDC00',
      logo: '🦚',
      drivers: [
        {
          name: 'Allan',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/esb9gnrt_Allan.jpg',
          number: 14,
          objectPos: '50% 20%'
        },
        {
          name: 'Lennarth',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/pcvmgwaa_Lennarth.jpg',
          number: 18,
          objectPos: '50% 30%'
        }
      ]
    },
    {
      name: 'BWT Alpine F1 Team',
      color: '#FF87BC',
      accentColor: '#0093CC',
      logo: '🏔️',
      drivers: [
        {
          name: 'Christopher',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/l6s0zvdi_Christopher.jpg',
          number: 10
        },
        {
          name: 'Noah',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/ugpj1585_Noah.jpg',
          number: 31
        }
      ]
    },
    {
      name: 'Williams Racing',
      color: '#64C4FF',
      accentColor: '#00295A',
      logo: '🔵',
      drivers: [
        {
          name: 'Mikkel',
          image: 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/3kxydne2_Gemini_Generated_Image_83qvov83qvov83qv.png',
          number: 23
        },
        {
          name: 'Oscar D.',
          image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/93937d76_Oscar%20D.jpg',
          number: 2,
          zoomOut: true
        }
      ]
    },
    {
      name: 'MoneyGram Haas F1 Team',
      color: '#B6BABD',
      accentColor: '#E10600',
      logo: '🇺🇸',
      drivers: [
        {
          name: 'Benny',
          image: 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/czjh4og7_Gemini_Generated_Image_w0zijow0zijow0zi.png',
          number: 20,
          objectPos: '50% 30%'
        },
        {
          name: 'Blichfeldt',
          image: 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/whcxo8ql_Gemini_Generated_Image_mhekr2mhekr2mhek.png',
          number: 5
        },
        {
          name: 'Guldbrandsen',
          image: 'https://customer-assets.emergentagent.com/job_7cd94266-aaca-42b4-8274-f9ae4208543e/artifacts/2yec949a_IMG_2494.png',
          number: 27,
          objectPos: '50% 20%'
        },
        {
          name: 'Lasse',
          image: 'https://customer-assets.emergentagent.com/job_7cd94266-aaca-42b4-8274-f9ae4208543e/artifacts/e9sj6l8m_IMG_2496.png',
          number: 77
        }
      ]
    }
  ];

  // Flatten all drivers with team info
  const allDrivers = teams.flatMap(team => 
    team.drivers.map(driver => ({ ...driver, team: team.name, teamColor: team.color }))
  );

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Teams & Kørere</h2>
        <p className="text-gray-400">Gardinbussen Championship 2025</p>
      </div>

      {/* All Drivers Grid - 4 columns */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {allDrivers.map((driver) => (
          <div 
            key={driver.name}
            className="relative group"
          >
            {/* Driver Card */}
            <div 
              className="bg-gray-800/50 rounded-xl overflow-hidden border-2 hover:border-gray-500 transition-all duration-300 hover:scale-[1.03]"
              style={{ borderColor: driver.teamColor }}
            >
              {/* Driver Image */}
              <div className="relative aspect-square overflow-hidden">
                <img 
                  src={driver.image}
                  alt={driver.name}
                  className={`w-full h-full object-cover ${driver.zoomOut ? 'scale-75 object-top' : ''} ${driver.cropTop ? 'object-top' : ''}`}
                  style={driver.objectPos ? { objectPosition: driver.objectPos } : {}}
                />
                {/* Number overlay */}
                <div 
                  className="absolute top-2 right-2 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-lg md:text-xl font-bold text-white shadow-lg"
                  style={{ backgroundColor: driver.teamColor }}
                >
                  {driver.number}
                </div>
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                {/* Driver name overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <h4 className="text-lg md:text-xl font-bold text-white truncate">{driver.name}</h4>
                  <p className="text-gray-300 text-xs md:text-sm mt-0.5 truncate">{driver.team}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Team Colors Legend */}
      <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
        <h4 className="text-lg font-semibold text-white mb-4">Holdfarver</h4>
        <div className="flex flex-wrap gap-4">
          {teams.map(team => (
            <div key={team.name} className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: team.color }}
              />
              <span className="text-gray-300 text-sm">{team.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Pit Crew Component - Montør Review Competition
const PitCrewLeaderboard = () => {
  const pitCrewMembers = [
    {
      name: 'Terkel',
      image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/oflc1n1d_Terkel.jpg',
      team: 'Mercedes-AMG Petronas',
      teamColor: '#00D2BE',
      weeklyPoints: { trustpilot: 2, google: 1, internal: 4 },
      championshipPoints: 180
    },
    {
      name: 'David',
      image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/gvezuqd9_David.jpg',
      team: 'Mercedes-AMG Petronas',
      teamColor: '#00D2BE',
      weeklyPoints: { trustpilot: 1, google: 2, internal: 3 },
      championshipPoints: 150
    },
    {
      name: 'Adrian',
      image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/ik80m10h_Adrian.jpg',
      team: 'Oracle Red Bull Racing',
      teamColor: '#1B3D67',
      weeklyPoints: { trustpilot: 3, google: 1, internal: 2 },
      championshipPoints: 200
    },
    {
      name: 'Lauge',
      image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/g1j0y1ic_Lauge.jpg',
      team: 'Oracle Red Bull Racing',
      teamColor: '#1B3D67',
      weeklyPoints: { trustpilot: 1, google: 1, internal: 5 },
      championshipPoints: 140
    },
    {
      name: 'Malthe',
      image: 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/81invhzi_Gemini_Generated_Image_vcwoy6vcwoy6vcwo.png',
      team: 'McLaren F1 Team',
      teamColor: '#FF8700',
      weeklyPoints: { trustpilot: 2, google: 2, internal: 3 },
      championshipPoints: 170
    },
    {
      name: 'Mika',
      image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/3nysjvh2_Mika.jpg',
      team: 'MoneyGram Haas F1 Team',
      teamColor: '#B6BABD',
      weeklyPoints: { trustpilot: 0, google: 1, internal: 4 },
      championshipPoints: 110
    },
    {
      name: 'Jakob',
      image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/rjqwa466_Emil.jpg',
      team: 'Scuderia Ferrari',
      teamColor: '#DC0000',
      weeklyPoints: { trustpilot: 1, google: 1, internal: 3 },
      championshipPoints: 120
    },
    {
      name: 'Ole',
      image: 'https://customer-assets.emergentagent.com/job_a480aa23-943d-4ec3-aae2-e9fc4bc09b40/artifacts/3oqub55e_Ole.jpg',
      team: 'McLaren F1 Team',
      teamColor: '#FF8700',
      weeklyPoints: { trustpilot: 2, google: 0, internal: 3 },
      championshipPoints: 130
    },
    {
      name: 'Jakob',
      image: 'https://customer-assets.emergentagent.com/job_d4f76a48-964f-4ebf-bf16-ef5ca706cea7/artifacts/fvvl7j5h_Gemini_Generated_Image_oidbldoidbldoidb.png',
      team: 'Aston Martin Aramco',
      teamColor: '#006F62',
      weeklyPoints: { trustpilot: 1, google: 1, internal: 3 },
      championshipPoints: 100
    },
    {
      name: 'Jonns',
      image: 'https://customer-assets.emergentagent.com/job_b094b34a-c4b0-4aa6-8e4e-a622e07c1b8e/artifacts/tv6xy9cn_Gemini_Generated_Image_9tpn239tpn239tpn.png',
      team: 'McLaren F1 Team',
      teamColor: '#FF8700',
      weeklyPoints: { trustpilot: 0, google: 0, internal: 0 },
      championshipPoints: 0
    }
  ];

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">🔧 Pit Crew 🔧</h2>
        <p className="text-gray-400">Vores mekanikere</p>
      </div>

      {/* Pit Crew Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {pitCrewMembers.map((member) => (
          <div 
            key={member.name}
            className="relative group"
          >
            <div 
              className="bg-gray-800/50 rounded-xl overflow-hidden border-2 hover:border-gray-500 transition-all duration-300 hover:scale-[1.03]"
              style={{ borderColor: member.teamColor }}
            >
              <div className="relative aspect-square overflow-hidden">
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                  style={{
                    ...(member.objectPos ? { objectPosition: member.objectPos } : {}),
                    ...(member.imageScale ? { transform: `scale(${member.imageScale})`, transformOrigin: member.imageOrigin || 'center 20%' } : {})
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <h4 className="text-lg md:text-xl font-bold text-white truncate">{member.name}</h4>
                  <p className="text-gray-300 text-xs md:text-sm mt-0.5 truncate">{member.team}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Pit Crew Weekly Standings Component
const PitCrewWeeklyStandings = ({ data }) => {
  const members = data || [];

  return (
    <div className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden">
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 p-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          Pit Crew - Ugens Stilling
        </h3>
        <p className="text-orange-100/70 text-xs mt-1">5-stjernet anmeldelse = 10 point</p>
      </div>
      <div className="divide-y divide-gray-700/50">
        {members.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">Ingen anmeldelser denne uge</div>
        ) : members.map((member, idx) => (
          <div key={`${member.name}-${idx}`} className="p-3 flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
              idx === 0 ? 'bg-yellow-500 text-black' : idx === 1 ? 'bg-gray-300 text-black' : idx === 2 ? 'bg-orange-700 text-white' : 'bg-gray-700 text-gray-300'
            }`}>
              {member.position || idx + 1}
            </div>
            <span className="text-white font-medium flex-1">{member.name}</span>
            <div className="flex items-center gap-2">
              {member.five_star_count > 0 && <span className="text-yellow-400 text-sm">{member.five_star_count} x ★</span>}
              {member.trustpilot_points > 0 && <span className="text-green-400 text-sm">TP: {member.trustpilot_points}p</span>}
              <span className="text-orange-400 font-bold text-sm">{member.points} pts</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Pit Crew Championship Standings Component  
const PitCrewChampionshipStandings = ({ data, calendar }) => {
  const members = data || [];
  const [selectedRace, setSelectedRace] = React.useState('total');
  const [expandedMember, setExpandedMember] = React.useState(null);

  const completedRaces = (calendar || []).filter(r => r.status === 'completed');
  const activeRace = (calendar || []).find(r => r.status === 'active');

  const displayData = React.useMemo(() => {
    if (selectedRace === 'total') {
      return members.map(m => ({ ...m, display_points: m.total_points || 0 }));
    }
    const weekNum = parseInt(selectedRace);
    const rows = [];
    members.forEach(m => {
      const entry = (m.races || []).find(r => r.week === weekNum);
      if (entry) {
        rows.push({
          name: m.name,
          internal_five_stars: entry.internal_five_stars || 0,
          internal_points: entry.internal_points || 0,
          trustpilot_five_stars: entry.trustpilot_five_stars || 0,
          trustpilot_points: entry.trustpilot_points || 0,
          display_points: entry.points || 0
        });
      }
    });
    rows.sort((a, b) => b.display_points - a.display_points);
    rows.forEach((r, i) => { r.position = i + 1; });
    return rows;
  }, [selectedRace, members]);

  const raceName = (calendar || []).find(r => String(r.week_number) === selectedRace)?.race_name;

  return (
    <div className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 to-purple-500 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-white">Pit Crew - Mesterskabsstilling</h3>
          <p className="text-purple-100/70 text-xs mt-1">Trustpilot 5★ = 20p | Intern 5★ = 10p</p>
        </div>
        {(completedRaces.length > 0 || activeRace) && (
          <select
            data-testid="pitcrew-championship-race-select"
            value={selectedRace}
            onChange={(e) => { setSelectedRace(e.target.value); setExpandedMember(null); }}
            className="bg-purple-900/50 border border-purple-400/40 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white"
          >
            <option value="total">Samlet stilling</option>
            {completedRaces.map(r => (
              <option key={r.week_number} value={String(r.week_number)}>
                Løb {r.week_number - 15} — {r.race_name} (Uge {r.week_number})
              </option>
            ))}
            {activeRace && (
              <option value={String(activeRace.week_number)}>
                Løb {activeRace.week_number - 15} — {activeRace.race_name} (Uge {activeRace.week_number}) — Live
              </option>
            )}
          </select>
        )}
      </div>
      {selectedRace !== 'total' && raceName && (
        <p className="text-gray-400 text-xs px-4 pt-3">{raceName} — Uge {selectedRace}</p>
      )}
      <div className="p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 border-b border-gray-700">
              <th className="text-left py-2 px-2">Pos</th>
              <th className="text-left py-2">Montør</th>
              <th className="text-right py-2 px-2 text-green-400">Trustpilot</th>
              <th className="text-right py-2 px-2 text-purple-400">Intern</th>
              <th className="text-right py-2 px-2 text-orange-400">
                {selectedRace === 'total' ? 'Total' : 'Point'}
              </th>
            </tr>
          </thead>
          <tbody>
            {displayData.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-6 text-gray-500">Ingen data endnu</td></tr>
            ) : displayData.map((m, idx) => {
              const isTotal = selectedRace === 'total';
              const tp = isTotal ? (m.trustpilot_points || 0) : (m.trustpilot_points || 0);
              const intern = isTotal ? (m.total_points - tp) : (m.internal_points || 0);
              const isExpanded = isTotal && expandedMember === m.name;
              const memberRaces = (members.find(x => x.name === m.name)?.races) || [];
              const pos = m.position || idx + 1;
              return (
                <React.Fragment key={idx}>
                  <tr
                    className={`border-b border-gray-800 ${pos <= 3 ? 'bg-orange-500/5' : ''} ${isTotal ? 'cursor-pointer hover:bg-white/5' : ''}`}
                    onClick={() => {
                      if (!isTotal) return;
                      setExpandedMember(expandedMember === m.name ? null : m.name);
                    }}
                    data-testid={`pitcrew-championship-row-${m.name?.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <td className="py-2 px-2">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        pos === 1 ? 'bg-yellow-500 text-black' : pos === 2 ? 'bg-gray-300 text-black' : pos === 3 ? 'bg-orange-700 text-white' : 'bg-gray-700 text-gray-300'
                      }`}>{pos}</span>
                    </td>
                    <td className="py-2 text-white font-medium">
                      <span className="flex items-center gap-2">
                        {m.name}
                        {isTotal && (
                          <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                      </span>
                    </td>
                    <td className="py-2 text-right px-2 text-green-400">{tp > 0 ? tp : '-'}</td>
                    <td className="py-2 text-right px-2 text-purple-400">{intern > 0 ? intern : '-'}</td>
                    <td className="py-2 text-right px-2 text-orange-400 font-bold">{m.display_points || 0}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-black/40 border-b border-gray-800">
                      <td colSpan={5} className="p-4">
                        <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Løbsberegning — {m.name}</div>
                        {memberRaces.length === 0 ? (
                          <div className="text-sm text-white/40 italic">Ingen afsluttede løb endnu.</div>
                        ) : (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-white/40 border-b border-white/10">
                                <th className="text-left py-1.5 px-2">Løb</th>
                                <th className="text-right py-1.5 px-2">Intern 5★</th>
                                <th className="text-right py-1.5 px-2 text-purple-400">Intern pt</th>
                                <th className="text-right py-1.5 px-2">Trustpilot 5★</th>
                                <th className="text-right py-1.5 px-2 text-green-400">Trustpilot pt</th>
                                <th className="text-right py-1.5 px-2 text-orange-400">Point</th>
                              </tr>
                            </thead>
                            <tbody>
                              {memberRaces.map((r, ri) => (
                                <tr key={ri} className="border-b border-white/[0.04]">
                                  <td className="py-1.5 px-2 text-white/80">
                                    {r.race_name || `Uge ${r.week}`}
                                    <span className="text-white/30 ml-1">(Uge {r.week})</span>
                                    {r.status === 'active' && (
                                      <span className="ml-2 text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">Live</span>
                                    )}
                                  </td>
                                  <td className="py-1.5 px-2 text-right text-white/60">{r.internal_five_stars || 0}</td>
                                  <td className="py-1.5 px-2 text-right text-purple-400">{r.internal_points || 0}</td>
                                  <td className="py-1.5 px-2 text-right text-white/60">{r.trustpilot_five_stars || 0}</td>
                                  <td className="py-1.5 px-2 text-right text-green-400">{r.trustpilot_points || 0}</td>
                                  <td className="py-1.5 px-2 text-right text-orange-400 font-bold">{r.points || 0}</td>
                                </tr>
                              ))}
                              <tr className="bg-orange-500/5">
                                <td colSpan={5} className="py-2 px-2 text-right text-white/70 font-semibold">Samlet</td>
                                <td className="py-2 px-2 text-right text-orange-400 font-bold">
                                  {memberRaces.reduce((sum, r) => sum + (r.points || 0), 0)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Biggest Order of the Week Component
const BiggestOrderBanner = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/f1/biggest-order');
        setData(res.data);
      } catch (e) {
        console.error('Failed to fetch biggest order:', e);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const currentWeek = data?.current_week;
  const order = data?.biggest_orders?.[String(currentWeek)];

  return (
    <div className="bg-gradient-to-r from-green-900/40 via-gray-900/60 to-green-900/40 rounded-xl p-5 border border-green-500/30 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-green-400 text-sm font-medium uppercase tracking-wider flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Ugens Største Ordre — +5 bonus point
          </div>
          <div className="text-gray-400 text-xs mt-1">Uge {currentWeek || '-'}</div>
        </div>

        {order ? (
          <div className="flex items-center gap-4">
            {order.salesperson_name && (
              <img
                src={getDriverImage(order.salesperson_name)}
                alt={order.salesperson_name}
                className="w-14 h-14 rounded-full object-cover border-2 border-green-400 shadow-lg shadow-green-500/20"
              />
            )}
            <div className="text-right">
              {order.salesperson_name ? (
                <>
                  <div className="text-2xl font-bold text-green-400">{order.salesperson_name}</div>
                  <div className="text-white text-lg font-semibold">
                    {Number(order.order_grand_total).toLocaleString('da-DK')} kr.
                  </div>
                  <div className="text-gray-400 text-xs">Ordre #{order.order_number}</div>
                </>
              ) : (
                <>
                  <div className="text-white text-lg font-semibold">
                    {Number(order.order_grand_total).toLocaleString('da-DK')} kr.
                  </div>
                  <div className="text-gray-400 text-xs">Ordre #{order.order_number} — sælger registreres snart</div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="text-gray-500 text-sm">Ingen ordrer endnu denne uge</div>
        )}
      </div>
    </div>
  );
};

// Biggest Orders History for Standings tab
const BiggestOrdersHistory = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/f1/biggest-order');
        setData(res.data);
      } catch (e) {
        console.error('Failed to fetch biggest orders:', e);
      }
    };
    fetchData();
  }, []);

  const weeks = data?.competition_weeks || [];
  const currentWeek = data?.current_week;
  const orders = data?.biggest_orders || {};

  return (
    <div className="bg-gray-900/50 rounded-xl border border-green-700/50 overflow-hidden">
      <div className="bg-gradient-to-r from-green-700 to-green-600 p-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Zap className="w-5 h-5" />
          Største Ordre pr. Uge — +5 bonus point
        </h3>
      </div>
      <div className="divide-y divide-gray-700/50">
        {weeks.map((week) => {
          const order = orders[String(week)];
          const isCurrent = week === currentWeek;
          const isPast = week < currentWeek;
          const isFuture = week > currentWeek;

          return (
            <div key={week} className={`p-3 flex items-center gap-3 ${isCurrent ? 'bg-green-900/20' : ''}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                isCurrent ? 'bg-green-600 text-white' : 
                isPast ? 'bg-gray-700 text-gray-300' : 
                'bg-gray-800 text-gray-500'
              }`}>
                {week}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${isCurrent ? 'text-green-400' : isPast ? 'text-white' : 'text-gray-500'}`}>
                    Uge {week}
                  </span>
                  {isCurrent && <span className="text-xs bg-green-600/30 text-green-400 px-2 py-0.5 rounded-full">Nu</span>}
                </div>
                {order ? (
                  <div className="text-gray-400 text-xs mt-0.5">
                    {order.salesperson_name 
                      ? `${order.salesperson_name} — Ordre #${order.order_number}`
                      : `Ordre #${order.order_number}`
                    }
                  </div>
                ) : (
                  <div className="text-gray-600 text-xs mt-0.5">
                    {isFuture ? 'Kommende' : isCurrent ? 'Ingen ordrer endnu' : '-'}
                  </div>
                )}
              </div>
              
              {order ? (
                <div className="flex items-center gap-3">
                  {order.salesperson_name && (
                    <img
                      src={getDriverImage(order.salesperson_name)}
                      alt={order.salesperson_name}
                      className="w-10 h-10 rounded-full object-cover border-2 border-green-500/50"
                    />
                  )}
                  <div className="text-right">
                    <div className="text-green-400 font-bold text-sm">
                      {Number(order.order_grand_total).toLocaleString('da-DK')} kr.
                    </div>
                    {order.salesperson_name && (
                      <div className="text-white text-xs font-medium">{order.salesperson_name}</div>
                    )}
                  </div>
                </div>
              ) : (
                <span className="text-gray-600 text-sm">-</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Tire Stack Visualization for Pit Crew
const PitCrewTireStack = ({ data }) => {
  const members = data || [];

  // Single tire SVG component
  const Tire = ({ color, size = 40 }) => (
    <svg width={size} height={size} viewBox="0 0 50 50">
      <circle cx="25" cy="25" r="23" fill="#1a1a1a" />
      <circle cx="25" cy="25" r="23" fill="none" stroke="#333" strokeWidth="3" strokeDasharray="4 3" />
      <circle cx="25" cy="25" r="17" fill="#2a2a2a" />
      <circle cx="25" cy="25" r="13" fill="#444" />
      <circle cx="25" cy="25" r="9" fill={color} />
      <circle cx="25" cy="25" r="4" fill="#222" />
      {[0, 72, 144, 216, 288].map(angle => (
        <circle
          key={angle}
          cx={25 + 6.5 * Math.cos(angle * Math.PI / 180)}
          cy={25 + 6.5 * Math.sin(angle * Math.PI / 180)}
          r="1.5"
          fill="#222"
        />
      ))}
      <ellipse cx="19" cy="19" rx="4" ry="3" fill="white" opacity="0.15" />
    </svg>
  );

  return (
    <div className="bg-gradient-to-b from-gray-900/80 to-gray-800/50 rounded-xl p-6 border border-orange-500/30">
      <div className="flex items-center justify-center gap-2 mb-6">
        <span className="text-2xl">🔧</span>
        <h3 className="text-lg font-bold text-white">Pit Crew - Ugens Stilling</h3>
        <span className="text-2xl">🔧</span>
      </div>

      <div className="flex justify-center items-end gap-3 md:gap-6 flex-wrap">
        {members.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">Ingen data endnu</p>
        ) : members.map((member, idx) => {
          const colors = ['#FFD700', '#C0C0C0', '#CD7F32', '#FF8700', '#00D2BE', '#1B3D67', '#DC0000', '#006F62', '#B6BABD'];
          const color = colors[idx % colors.length];
          const tireCount = member.five_star_count || 0;
          return (
            <div key={member.name} className="flex flex-col items-center">
              <div className="mb-2 flex flex-col items-center gap-0.5">
                {member.points >= 5 ? (
                  Array.from({ length: Math.min(Math.floor(member.points / 5), 20) }).map((_, i) => (
                    <Tire key={i} color={color} size={32} />
                  ))
                ) : null}
              </div>
              <div className="w-12 h-2 rounded-sm mb-1" style={{ backgroundColor: color }} />
              <div className="text-center">
                <div className="text-white text-xs font-medium truncate w-16">{member.name.split(' ')[0]}</div>
                <div className="text-orange-400 text-xs font-bold">{member.points}p</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-700 flex flex-wrap justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <span className="text-green-400">Trustpilot 5★:</span>
          <span className="text-white font-bold">20p</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-yellow-400">Intern 5★:</span>
          <span className="text-white font-bold">10p</span>
        </div>
      </div>
    </div>
  );
};

// Rules Tab Component
const RulesTab = () => {
  const [activeRule, setActiveRule] = useState('konsulenter');

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Regelsæt</h2>
        <p className="text-gray-400">Regler for F1-konkurrencen 2026</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex justify-center gap-3 mb-6">
        <button
          onClick={() => setActiveRule('konsulenter')}
          className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${
            activeRule === 'konsulenter'
              ? 'bg-blue-600 text-white shadow-lg'
              : 'text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700'
          }`}
        >
          Salgskonkurrence (Konsulenter)
        </button>
        <button
          onClick={() => setActiveRule('montoerer')}
          className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${
            activeRule === 'montoerer'
              ? 'bg-orange-600 text-white shadow-lg'
              : 'text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700'
          }`}
        >
          Montørkonkurrence (Pit Crew)
        </button>
      </div>

      {activeRule === 'konsulenter' ? (
        <div className="space-y-4 max-w-3xl mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-900/50 to-gray-900/50 rounded-xl p-6 border border-blue-500/30 text-center">
            <h3 className="text-xl font-bold text-white">Salgskonkurrence F1 Edition</h3>
            <p className="text-blue-400 mt-1">Konsulenter</p>
          </div>

          {/* Periode */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-3">Periode</h4>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5"><Calendar className="w-4 h-4" /></span>Konkurrencen løber fra <strong className="text-white">13. april til 26. juni 2026</strong></li>
              <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5"><Flag className="w-4 h-4" /></span>Består af <strong className="text-white">11 ugentlige løb</strong></li>
              <li className="flex items-start gap-2"><span className="text-blue-400 mt-0.5"><Trophy className="w-4 h-4" /></span>Vinderen offentliggøres til firmafesten den <strong className="text-white">26. juni 2026</strong></li>
            </ul>
          </div>

          {/* Format */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-3">Format</h4>
            <ul className="space-y-1.5 text-gray-300 text-sm">
              <li>Hver uge udgør ét "løb"</li>
              <li>Alle konsulenter deltager individuelt</li>
              <li>Resultater opgøres ugentligt</li>
            </ul>
          </div>

          {/* Måleparameter */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-3">Måleparameter</h4>
            <div className="text-gray-300 text-sm space-y-2">
              <p>Konkurrencen afgøres på <strong className="text-white">DPT (Dækningsbidrag pr. time)</strong></p>
              <div className="bg-gray-800 rounded-lg p-3 font-mono text-center text-white">
                DPT = DB / antal timer konsulenten er ansat til
              </div>
              <p>Kun ordre i <strong className="text-yellow-400">"Igangværende"</strong> status tæller med</p>
            </div>
          </div>

          {/* Pointsystem */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-3">Pointsystem</h4>
            <p className="text-gray-400 text-sm mb-3">Point gives hver uge baseret på placering:</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                { pos: '1.', pts: 25, color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' },
                { pos: '2.', pts: 20, color: 'text-gray-300 bg-gray-500/10 border-gray-500/30' },
                { pos: '3.', pts: 15, color: 'text-gray-300 bg-gray-500/10 border-gray-500/30' },
                { pos: '4.', pts: 12, color: 'text-gray-300 bg-gray-700/50 border-gray-600/30' },
                { pos: '5.', pts: 10, color: 'text-gray-300 bg-gray-700/50 border-gray-600/30' },
                { pos: '6.', pts: 8, color: 'text-gray-400 bg-gray-700/30 border-gray-700/30' },
                { pos: '7.', pts: 6, color: 'text-gray-400 bg-gray-700/30 border-gray-700/30' },
                { pos: '8.', pts: 4, color: 'text-gray-400 bg-gray-700/30 border-gray-700/30' },
                { pos: '9.', pts: 2, color: 'text-gray-400 bg-gray-700/30 border-gray-700/30' },
                { pos: '10.', pts: 1, color: 'text-gray-400 bg-gray-700/30 border-gray-700/30' },
              ].map(p => (
                <div key={p.pos} className={`rounded-lg p-2 text-center border ${p.color}`}>
                  <div className="font-bold text-lg">{p.pts}</div>
                  <div className="text-xs opacity-70">{p.pos} plads</div>
                </div>
              ))}
            </div>
            <p className="text-gray-500 text-xs mt-2">Øvrige placeringer — 0 point</p>
          </div>

          {/* Bonus */}
          <div className="bg-gradient-to-r from-green-900/30 to-gray-900/50 rounded-xl p-5 border border-green-500/30">
            <h4 className="text-green-400 font-bold text-sm uppercase tracking-wider mb-2">Bonus — Fastest Lap</h4>
            <p className="text-gray-300 text-sm"><strong className="text-green-400">+5 point</strong> til ugens største ordre</p>
          </div>

          {/* Samlet vinder */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-3">Samlet Vinder</h4>
            <div className="text-gray-300 text-sm space-y-2">
              <p>Vinderen er den konsulent med <strong className="text-white">flest point efter 11 løb</strong></p>
              <p className="text-gray-400">Ved pointlighed:</p>
              <ol className="list-decimal list-inside text-gray-400 text-sm space-y-1 ml-2">
                <li>Flest 1. pladser</li>
                <li>Flest 2. pladser</li>
                <li>Flest 3. pladser (osv.)</li>
              </ol>
            </div>
          </div>

          {/* Regler */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-red-500/20">
            <h4 className="text-red-400 font-bold text-sm uppercase tracking-wider mb-3">Regler</h4>
            <ul className="space-y-1.5 text-gray-300 text-sm">
              <li>Kun ordre i "Igangværende" status er gyldige</li>
              <li>Ordren skal være registreret i konkurrenceperioden</li>
              <li>Det er tilladt og anbefalet at følge op på tidligere tilbud</li>
              <li>Hvis man har ferie, er syg eller på anden måde fraværende i konkurrenceperioden, deltager man stadig i konkurrencen</li>
              <li>Snyd eller manipulation medfører diskvalifikation</li>
              <li>Ledelsen træffer endelig afgørelse ved tvivl</li>
            </ul>
          </div>

          {/* Særligt fokus */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-3">Særligt Fokus</h4>
            <ul className="space-y-1.5 text-gray-300 text-sm">
              <li>Konsulenter må gerne aktivt kontakte tidligere tilbud og leads</li>
              <li>Formålet er at skabe aktivitet og lukke ordre fra dag 1</li>
            </ul>
          </div>

          {/* Kort sagt */}
          <div className="bg-gradient-to-r from-blue-900/30 to-gray-900/50 rounded-xl p-5 border border-blue-500/20">
            <h4 className="text-blue-400 font-bold text-sm uppercase tracking-wider mb-3">Kort Sagt</h4>
            <div className="flex flex-wrap gap-3">
              {['11 løb', 'Fokus på effektivitet (DPT)', 'Hurtig start med gamle leads', 'Klar finale til firmafest'].map(item => (
                <span key={item} className="bg-blue-500/10 text-blue-300 px-3 py-1.5 rounded-full text-sm border border-blue-500/20">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4 max-w-3xl mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-900/50 to-gray-900/50 rounded-xl p-6 border border-orange-500/30 text-center">
            <h3 className="text-xl font-bold text-white">Montørkonkurrence — Trustpilot Edition</h3>
            <p className="text-orange-400 mt-1">Pit Crew</p>
          </div>

          {/* Periode */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-orange-400 font-bold text-sm uppercase tracking-wider mb-3">Periode</h4>
            <ul className="space-y-2 text-gray-300 text-sm">
              <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5"><Calendar className="w-4 h-4" /></span>Konkurrencen løber fra <strong className="text-white">13. april til 26. juni 2026</strong></li>
              <li className="flex items-start gap-2"><span className="text-orange-400 mt-0.5"><Trophy className="w-4 h-4" /></span>Vinderen offentliggøres til firmafesten den <strong className="text-white">26. juni 2026</strong></li>
            </ul>
          </div>

          {/* Format */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-orange-400 font-bold text-sm uppercase tracking-wider mb-3">Format</h4>
            <ul className="space-y-1.5 text-gray-300 text-sm">
              <li>Alle montører deltager individuelt</li>
              <li>Point optjenes løbende gennem hele perioden</li>
            </ul>
          </div>

          {/* Måleparameter */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-orange-400 font-bold text-sm uppercase tracking-wider mb-3">Måleparameter</h4>
            <p className="text-gray-300 text-sm">Konkurrencen afgøres på <strong className="text-white">antal point fra kundeanmeldelser</strong></p>
          </div>

          {/* Pointsystem */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-orange-400 font-bold text-sm uppercase tracking-wider mb-3">Pointsystem</h4>
            <div className="space-y-4">
              {/* Trustpilot */}
              <div className="bg-green-900/20 rounded-lg p-4 border border-green-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-400 font-bold text-sm">Trustpilot anmeldelser</span>
                  <span className="text-white font-bold text-lg">20 point</span>
                </div>
                <p className="text-gray-300 text-sm">1 stk. 5-stjernet anmeldelse = <strong className="text-green-400">20 point</strong></p>
                <p className="text-gray-400 text-xs mt-2">Gælder for: Gardinbussen-Prisgaranti.dk, Gardinbus.nu, Bestil-gardinbussen</p>
              </div>

              {/* Internt system */}
              <div className="bg-purple-900/20 rounded-lg p-4 border border-purple-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-purple-400 font-bold text-sm">Internt anmeldelsessystem</span>
                  <span className="text-white font-bold text-lg">10 point</span>
                </div>
                <p className="text-gray-300 text-sm">1 stk. 5-stjernet vurdering (montering) = <strong className="text-purple-400">10 point</strong></p>
                <p className="text-gray-400 text-xs mt-2">Gælder når kunden giver 5 stjerner på monteringsoplevelsen via automatisk anmodning</p>
              </div>
            </div>
          </div>

          {/* Regler */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-red-500/20">
            <h4 className="text-red-400 font-bold text-sm uppercase tracking-wider mb-3">Regler</h4>
            <ul className="space-y-1.5 text-gray-300 text-sm">
              <li>Anmeldelsen skal komme fra en kunde, hvor montøren har udført en montering</li>
              <li>Anmeldelsen/vurderingen skal være oprettet i konkurrenceperioden</li>
              <li>Hvis to montører har været på samme opgave, og kunden giver en 5-stjernet anmeldelse, <strong className="text-white">deles pointene ligeligt</strong> mellem montørerne</li>
              <li>Det er montørens ansvar at opfordre kunden til at anmelde oplevelsen</li>
              <li>Snyd eller manipulation medfører diskvalifikation</li>
              <li>Ledelsen træffer endelig afgørelse ved tvivl</li>
            </ul>
          </div>

          {/* Vinder */}
          <div className="bg-gray-900/50 rounded-xl p-5 border border-gray-700">
            <h4 className="text-orange-400 font-bold text-sm uppercase tracking-wider mb-3">Vinder</h4>
            <ul className="space-y-1.5 text-gray-300 text-sm">
              <li>Der kåres en <strong className="text-white">ugevinder</strong> hver uge — montøren med flest point den pågældende uge</li>
              <li>Den samlede vinder er den montør med <strong className="text-white">flest point ved konkurrencens afslutning</strong></li>
            </ul>
          </div>

          {/* Kort sagt */}
          <div className="bg-gradient-to-r from-orange-900/30 to-gray-900/50 rounded-xl p-5 border border-orange-500/20">
            <h4 className="text-orange-400 font-bold text-sm uppercase tracking-wider mb-3">Kort Sagt</h4>
            <div className="flex flex-wrap gap-3">
              {['20 point = Trustpilot (aktiv indsats)', '10 point = internt system (automatisk feedback)', 'Ugevinder kåres hver uge', 'Fokus på kundeoplevelse'].map(item => (
                <span key={item} className="bg-orange-500/10 text-orange-300 px-3 py-1.5 rounded-full text-sm border border-orange-500/20">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F1Race = () => {
  const navigate = useNavigate();
  const [raceData, setRaceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('race'); // 'race' or 'teams'

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/f1/race-data-public');
      setRaceData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching F1 data:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading && !raceData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="flex items-center justify-center h-96">
          <div className="text-white text-xl">Indlæser F1 data...</div>
        </div>
      </div>
    );
  }

  const currentRace = raceData?.current_race;
  const racers = currentRace?.racers || [];
  const standings = raceData?.championship_standings || [];
  const calendar = raceData?.race_calendar || [];
  const pitCrew = raceData?.pit_crew || { current_week: [], standings: [] };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Header with F1 Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-5 mb-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_b471bfc1-d520-45f4-877f-1cd6fc8ad74a/artifacts/dus55wts_F1%20logo.png"
              alt="F1 Logo"
              className="h-16 md:h-20 w-auto object-contain"
              data-testid="f1-logo"
            />
            <div className="h-12 w-px bg-gradient-to-b from-transparent via-red-500 to-transparent" />
            <h1 className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-white to-red-500">
              GARDINBUSSEN CHAMPIONSHIP
            </h1>
          </div>
          <p className="text-gray-400">
            {raceData?.competition?.start_date?.split('-').reverse().join('/')} - {raceData?.competition?.end_date?.split('-').reverse().join('/')}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-800/50 rounded-xl p-1.5 border border-gray-700 flex gap-2 flex-wrap justify-center">
            <button
              onClick={() => setActiveTab('race')}
              className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === 'race'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Flag className="w-4 h-4" />
              Løb
            </button>
            <button
              onClick={() => setActiveTab('standings')}
              className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === 'standings'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Trophy className="w-4 h-4" />
              Stilling
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === 'calendar'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Kalender
            </button>
            <button
              onClick={() => setActiveTab('teams')}
              className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === 'teams'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Users className="w-4 h-4" />
              Teams & Kørere
            </button>
            <button
              onClick={() => setActiveTab('pitcrew')}
              className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === 'pitcrew'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Zap className="w-4 h-4" />
              Pit Crew
            </button>
            <button
              onClick={() => setActiveTab('regler')}
              className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === 'regler'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Flag className="w-4 h-4" />
              Regler
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'teams' ? (
          <TeamsAndDrivers />
        ) : activeTab === 'calendar' ? (
          <RaceCalendarTab races={calendar} />
        ) : activeTab === 'pitcrew' ? (
          <PitCrewLeaderboard />
        ) : activeTab === 'regler' ? (
          <RulesTab />
        ) : activeTab === 'standings' ? (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Stillinger</h2>
              <p className="text-gray-400">Kørere og Pit Crew</p>
            </div>
            
            {/* Driver Standings */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Flag className="w-5 h-5 text-red-500" />
                Kørere
              </h3>
              {/* Driver Championship Leader */}
              {(racers?.[0] || standings?.[0]) && (
                <div className="bg-gradient-to-r from-red-900/50 via-gray-900/50 to-red-900/50 rounded-xl p-4 mb-4 border border-red-500/30">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <div className="text-red-400 text-sm font-medium uppercase tracking-wider">Fører mesterskabet</div>
                      <div className="text-xl font-bold text-white mt-1">
                        {standings?.[0]?.total_points > 0 ? `${standings[0].total_points} point` : `DPT: ${racers?.[0]?.dpt?.toLocaleString('da-DK') || 0}`}
                      </div>
                    </div>
                    {(() => {
                      const leaderName = standings?.[0]?.total_points > 0 ? standings[0].name : (racers?.[0]?.name || standings?.[0]?.name);
                      return (
                        <div className="flex items-center gap-4" data-testid="driver-championship-leader">
                          {getDriverImage(leaderName) && (
                            <img 
                              src={getDriverImage(leaderName)} 
                              alt={leaderName}
                              className="w-16 h-16 rounded-full object-cover border-2 border-yellow-400"
                              data-testid="driver-championship-leader-image"
                            />
                          )}
                          <div className="text-right">
                            <div className="text-gray-400 text-sm">Aktuel fører</div>
                            <div className="text-2xl font-bold text-yellow-400" data-testid="driver-championship-leader-name">
                              {leaderName}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Leaderboard racers={racers} title="Ugens Stilling" />
                <ChampionshipStandings standings={standings} calendar={calendar} />
              </div>
            </div>
            
            {/* Pit Crew Standings */}
            <div className="pt-6 border-t border-gray-700">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-500" />
                Pit Crew (Montører)
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PitCrewWeeklyStandings data={pitCrew.current_week} />
                <PitCrewChampionshipStandings data={pitCrew.standings} calendar={calendar} />
              </div>
            </div>

            {/* Biggest Orders per Week */}
            <div className="pt-6 border-t border-gray-700">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-500" />
                Største Ordre pr. Uge (+5 point)
              </h3>
              <BiggestOrdersHistory />
            </div>
          </div>
        ) : (
          <>
        {/* Current Race Header */}
        {currentRace && (
          <div className="bg-gradient-to-r from-red-900/50 via-gray-900/50 to-red-900/50 rounded-xl p-4 mb-6 border border-red-500/30">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-red-400 text-sm font-medium uppercase tracking-wider">
                  {currentRace.status === 'active' ? '🔴 LIVE RACE' : currentRace.status === 'completed' ? 'AFSLUTTET' : 'KOMMENDE'}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">{currentRace.race_name}</h2>
                <p className="text-gray-400">
                  {currentRace.start_date?.split('-').reverse().join('/')} - {currentRace.end_date?.split('-').reverse().join('/')}
                </p>
              </div>
              {currentRace.leader && currentRace.leader.dpt > 0 && (
                <div className="flex items-center gap-4">
                  {getDriverImage(currentRace.leader.name) && (
                    <img 
                      src={getDriverImage(currentRace.leader.name)} 
                      alt={currentRace.leader.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-yellow-400"
                    />
                  )}
                  <div className="text-right">
                    <div className="text-gray-400 text-sm">Aktuel fører</div>
                    <div className="text-2xl font-bold text-yellow-400">{currentRace.leader.name}</div>
                    <div className="text-green-400">DPT: {currentRace.leader.dpt?.toLocaleString('da-DK')}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Race Track */}
        <div className="bg-gray-900/50 rounded-xl p-6 mb-6 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Flag className="w-6 h-6 text-red-500" />
            Ugens Løb - Live Track
          </h3>
          
          <div className="relative">
            {/* Start/Finish banner */}
            <div className="absolute -top-2 right-0 bg-black text-white px-3 py-1 text-xs font-bold rounded">
              MÅL
            </div>
            
            {/* Race track with cars */}
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              {racers.map((racer, idx) => (
                <F1Car 
                  key={racer.name} 
                  racer={racer} 
                  index={idx}
                  isLeader={idx === 0}
                />
              ))}
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-6 mt-4 text-xs text-gray-400">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
              <span>1. plads</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-300 rounded-full" />
              <span>2. plads</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-amber-600 rounded-full" />
              <span>3. plads</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-3 h-3 text-green-400" />
              <span>DRS aktiveret</span>
            </div>
          </div>
        </div>

        {/* Podium for completed race */}
        {currentRace?.status === 'completed' && racers.length >= 3 && (
          <div className="bg-gradient-to-b from-gray-900/50 to-black/50 rounded-xl p-6 mb-6 border border-yellow-500/30">
            <h3 className="text-xl font-bold text-white text-center mb-2 flex items-center justify-center gap-2">
              <Medal className="w-6 h-6 text-yellow-500" />
              Ugens Podium
            </h3>
            <Podium racers={racers.slice(0, 3)} />
          </div>
        )}

        {/* Biggest Order Banner */}
        <BiggestOrderBanner />

        {/* Pit Crew Leader - only show when there's real data */}
        {(() => {
          // Don't show pit crew standings when no real data is available
          return null;
        })()}
        <PitCrewTireStack data={pitCrew.current_week} />
          </>
        )}

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8">
          Sidst opdateret: {raceData?.last_updated ? new Date(raceData.last_updated).toLocaleString('da-DK') : '-'}
        </div>
      </main>
    </div>
  );
};

export default F1Race;
